import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Moon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { LoginInput } from "@/components/LoginInput";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/useToast";
import logger from "@/utils/logger";
import {
  LOGIN_NO_ACCOUNT,
  LOGIN_SEND_ERROR,
  LOGIN_SUSPENDED,
  LOGIN_UNVERIFIED,
} from "@/utils/toastMessages";
import { rememberReturnPath } from "@/utils/postLoginRedirect";
import { useEffect, useState } from "react";
import { PageHead } from "@/components/PageHead";
import achieveOsIcon from "@/assets/images/login/achieveos-icon-violet.svg";
import achieveOsWordmarkWhite from "@/assets/images/login/achieveos-wordmark-white.svg";
import achieveOsWordmarkDark from "@/assets/images/login/achieveos-wordmark-dark.svg";
import sunIcon from "@/assets/images/login/icon-sun.svg";
import layersIcon from "@/assets/images/login/icon-layers.svg";
import calendarCheckIcon from "@/assets/images/login/icon-calendar-check.svg";
import userCheckIcon from "@/assets/images/login/icon-user-check.svg";
import chevronRightIcon from "@/assets/images/login/icon-chevron-right.svg";
import sproutIcon from "@/assets/images/login/icon-sprout.svg";
import awardIcon from "@/assets/images/login/icon-award.svg";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import { LoginButton } from "@/components/buttonVariants";
import { LinkedInBrandIcon } from "@/components/icons/icons";
import { useDarkMode } from "@/hooks/useDarkMode";
import { buildLinkedInAuthUrl } from "@/utils/linkedin";
import { EXPERT_SIGNUP_ROLE_PARAM, signupRoleFromParam } from "@/utils/roleUtils";
// DISABLED (multi-account): add-account intent helpers.
// import {
//   claimAddAccountIntentFromWindowName,
//   getAddAccountConnectTargetUserId,
//   isAddAccountMode,
// } from "@/utils/auth";
import { loginLinkedInClicked, loginEmailSubmitted } from "@/utils/tracking";

const loginSchema = z.object({
  email: z
    .string()
    .email({ message: "Please enter a valid email address" })
    .min(1, { message: "Email is required" }),
});

type LoginFormData = z.infer<typeof loginSchema>;

const HERO_BULLETS = [
  {
    icon: layersIcon,
    text: "Share your experience through Programs, PABs, co-Expert engagements and peer opportunities",
  },
  {
    icon: calendarCheckIcon,
    text: "Stay aligned through sessions, tasks, and guidance. Flexible commitment, average just 2–4 hours per month",
  },
  {
    icon: userCheckIcon,
    text: "Every Expert account includes an Achiever workspace for your own growth",
  },
];

const LoginPage = () => {
  const { sendLoginLink } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Router state covers in-app redirects (e.g. signup detecting an existing
  // account); a real invite-email click opens this page cold with no router
  // state, only query params -- so every field below falls back to the URL
  // (co-expert and achiever invite emails both send `?email=&join=&role=`).
  const email = location.state?.email || searchParams.get("email") || undefined;
  const roleParam = searchParams.get("role");
  const role =
    (location.state?.role as "achiever" | "advisor" | undefined) ||
    // `role=expert` means the advisor role.
    (roleParam ? signupRoleFromParam(roleParam) : undefined);
  const joinProgramId =
    (location.state?.joinProgramId as string | undefined) || searchParams.get("join") || undefined;
  const toastMessage = location.state?.toastMessage as string | undefined;
  const navigate = useNavigate();
  const { isDark, toggle } = useDarkMode();

  const [loading, setLoading] = useState(false);

  // DISABLED (multi-account): claimed the one-time add-account nonce from
  // window.name so this tab knew to link the new login to the opener's account.
  // useEffect(() => {
  //   const claimed = claimAddAccountIntentFromWindowName(window.name || "");
  //   if (claimed) {
  //     window.name = "";
  //   }
  // }, []);

  // Show a toast if redirected here from signup (e.g. account already exists).
  useEffect(() => {
    if (toastMessage) {
      showToast({ type: "error", message: toastMessage, duration: 4000 });
    }
    // Only on mount — toastMessage comes from router state which doesn't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sent here from a page that needs sign-in (ProtectedRoute puts it in
  // `from`), or with ?next= — go back there after the magic link signs them in.
  useEffect(() => {
    const from = location.state?.from as { pathname?: string; search?: string } | undefined;
    rememberReturnPath(
      from?.pathname ? `${from.pathname}${from.search ?? ""}` : searchParams.get("next"),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If arriving from an enrollment link that detected an existing account,
  // persist the program context so the post-login flow can join the program.
  useEffect(() => {
    if (joinProgramId) {
      localStorage.setItem("program_join_id", joinProgramId);
      localStorage.setItem("program_join_role", role || "achiever");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      email: email || "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    loginEmailSubmitted();
    try {
      await sendLoginLink(data.email);
      // The next screen is the same "check your email" step for everyone; for
      // the review account it renders a password field instead (handled there).
      navigate("/login-email-check", { state: { email: data.email, role } });
    } catch (error) {
      logger.error(error);
      // The endpoint distinguishes three failures — 404 no such account, 403
      // suspended, 400 unverified. All three used to be collapsed into one
      // generic "try again", so a suspended user was told to retry forever.
      //
      // Mapped by STATUS rather than echoing response.data.detail: the 404's
      // wording is "User not found", which is internal phrasing, and keying off
      // the code means the copy here survives a server-side rewording.
      const status = (error as { response?: { status?: number } })?.response?.status;

      const message =
        status === 403
          ? LOGIN_SUSPENDED
          : status === 404
            ? LOGIN_NO_ACCOUNT
            : status === 400
              ? LOGIN_UNVERIFIED
              : LOGIN_SEND_ERROR;

      showToast({
        type: "error",
        message,
        // Suspended and no-account both need a next step (contact support, or
        // sign up) rather than a re-read of a toast that has already faded.
        duration: status === 403 || status === 404 ? 8000 : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  // Cross-links to the two signup entry points. No org code is appended here —
  // an org-coded signup arrives via its own invite URL, and adding one to these
  // generic links would silently enrol a self-serve visitor into that org.
  const goToAchieverSignup = () => navigate("/signup");
  const goToExpertSignup = () => navigate(`/signup?role=${EXPERT_SIGNUP_ROLE_PARAM}`);

  return (
    <>
      <PageHead title="Login" description="Sign in to your AchieveOS account" />
      <EnvironmentBadge />

      <div className="flex min-h-screen flex-col bg-theme-login-page-bg bg-login-page bg-cover bg-top bg-no-repeat font-inter">
        {/* Header */}
        <header className="mx-auto flex w-full max-w-[1368px] items-center justify-between px-6 py-[22px]">
          <div className="flex h-[38.776px] items-center gap-[3px] pl-[3.87px]">
            <img src={achieveOsIcon} alt="" className="h-[29.89px] w-[31.021px]" />
            <img
              src={achieveOsWordmarkWhite}
              alt="AchieveOS"
              className="logo-dark-mode h-[19.244px] w-[122.754px]"
            />
            <img
              src={achieveOsWordmarkDark}
              alt="AchieveOS"
              className="logo-light-mode h-[19.244px] w-[122.754px]"
            />
          </div>

          <div className="flex items-center">
            <button
              type="button"
              onClick={goToAchieverSignup}
              className="hidden text-[13px] leading-[19.5px] text-theme-login-brand-text hover:underline sm:block"
            >
              Sign up as Achiever
            </button>
            <button
              type="button"
              onClick={goToExpertSignup}
              className="ml-6 hidden h-9 items-center gap-[5px] whitespace-nowrap rounded-[10px] border border-theme-login-header-btn-border px-[14px] text-[13px] font-semibold leading-[19.5px] text-theme-login-link-text transition-opacity hover:opacity-80 sm:flex"
            >
              <AwardIcon />
              Apply as an Expert
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-label="Toggle dark mode"
              className="ml-4 flex h-9 w-[37.5px] items-center justify-center rounded-[10px] border border-theme-login-toggle-border bg-theme-login-toggle-bg text-theme-login-brand-text transition-opacity hover:opacity-80"
            >
              {isDark ? (
                <img src={sunIcon} alt="" className="size-4" />
              ) : (
                <Moon size={16} aria-hidden="true" />
              )}
            </button>
          </div>
        </header>

        {/* Hero + sign-in card */}
        <main className="flex flex-1 items-center justify-center px-6 py-8 lg:py-10">
          <div className="flex w-full max-w-[1102px] items-center justify-center gap-12 xl:gap-[110px]">
            <section className="hidden min-w-0 max-w-[552px] flex-1 font-sarabun lg:block">
              <h1 className="text-[40px] font-bold xl:text-[50px] leading-none tracking-[-0.5px] text-theme-login-brand-text">
                One system for growth,
                <br />
                guidance, and progress.
              </h1>
              <p className="mt-[22px] text-[21px] font-semibold leading-[1.5] text-theme-login-hero-text">
                AchieveOS helps every user move from intention to progress with clarity.
              </p>
              <ul className="mt-[29px] w-[520px] max-w-full border-t border-theme-login-divider">
                {HERO_BULLETS.map(({ icon, text }) => (
                  <li
                    key={text}
                    className="flex items-center gap-[14px] border-b border-theme-login-divider py-5"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-theme-login-bullet-icon-bg">
                      <img src={icon} alt="" className="size-4" />
                    </span>
                    <span className="max-w-[443px] text-16 font-light leading-[1.25] text-theme-login-bullet-text">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <div className="w-full max-w-[440px] shrink-0 rounded-[10px] border border-theme-login-card-border bg-login-card px-6 pb-[37px] pt-[37px] shadow-login-card sm:px-[37px]">
              <h2 className="text-[22px] font-semibold leading-[33px] tracking-[-0.4px] text-theme-login-card-title">
                Welcome to AchieveOS
              </h2>
              <p className="mt-2 text-14 leading-[21.7px] text-theme-login-card-subtitle">
                Sign in to pick up where you left off — your PAB, Programs and goals are waiting.
              </p>

              {/* LinkedIn SSO */}
              <LoginButton
                variant="linkedin"
                className="mt-[27px]"
                leftIcon={<LinkedInBrandIcon className="block" />}
                onClick={() => {
                  loginLinkedInClicked();
                  // DISABLED (multi-account): add-account mode passed the
                  // opener's user ID here so the backend auto-connected
                  // both accounts after LinkedIn auth.
                  window.location.href = buildLinkedInAuthUrl("achiever", "", "login");
                }}
              >
                Continue with LinkedIn
              </LoginButton>

              <div className="mt-6 flex items-center gap-4">
                <div className="h-px flex-1 bg-theme-login-card-divider" />
                <span className="whitespace-nowrap text-12 leading-[18px] text-theme-login-card-divider-text">
                  or sign in with email
                </span>
                <div className="h-px flex-1 bg-theme-login-card-divider" />
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                <LoginInput
                  id="email"
                  type="email"
                  label="Email address"
                  placeholder="alex@company.com"
                  error={errors.email?.message}
                  {...register("email")}
                />

                <LoginButton
                  type="submit"
                  variant="primary"
                  isLoading={loading}
                  rightIcon={<img src={chevronRightIcon} alt="" className="block size-4" />}
                  className="mt-3"
                >
                  Send Sign In Link
                </LoginButton>
              </form>

              <div className="mt-6 flex items-center justify-center gap-3 border-t border-theme-login-divider pt-5 text-[13px] font-medium leading-[19.5px] text-theme-login-link-text">
                <button
                  type="button"
                  onClick={goToAchieverSignup}
                  className="flex items-center gap-1.5 hover:underline"
                >
                  <img src={sproutIcon} alt="" className="size-[14px]" />
                  Sign up as Achiever
                </button>
                <span className="h-[14px] w-px bg-theme-login-link-separator" aria-hidden="true" />
                <button
                  type="button"
                  onClick={goToExpertSignup}
                  className="flex items-center gap-1.5 hover:underline"
                >
                  <AwardIcon />
                  Apply as an Expert
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mx-auto flex w-[calc(100%-48px)] max-w-[1344px] items-center justify-between border-t border-theme-login-footer-border py-4 text-12 leading-[18px] text-theme-login-footer-text">
          <p>© {new Date().getFullYear()} Forward Share Ventures</p>
          <div className="flex items-center gap-4">
            <Link to="/legal/terms" className="hover:underline">
              Terms
            </Link>
            <Link to="/legal/privacy" className="hover:underline">
              Privacy
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

/** Award/ribbon glyph from the Figma frame — the source vector is drawn on its
 *  side, so it is rotated upright here exactly as the design does. */
const AwardIcon = () => (
  <span className="relative flex size-[14px] shrink-0 items-center justify-center">
    <img src={awardIcon} alt="" className="max-w-none rotate-[120deg]" />
  </span>
);

export default LoginPage;
